import { Action, Selector, State, StateContext } from '@ngxs/store';
import { Injectable, inject } from '@angular/core';
import {
  AddAppointment,
  AddAppointmentProduct,
  AddAppointmentService,
  AddAppointmentServiceProduct,
  AddAppointmentSpecService,
  AddEmployeeAppointment,
  CalendarPosition,
  CalendarTimeInterval,
  ChangeEmployee,
  ClearAddAppointmentSpecService,
  ClearAddEmployeeAppointment,
  ClearAppointmentService,
  CompleteAppointment,
  CompleteUpdateAppointment,
  EditAppointment,
  EditAppointmentService,
  EditEmployeeAppointment,
  GetCalendarEmployee,
  GetEvents,
  GetOnlineEvents,
  GetRoomCalendar,
  GetRoomEvents,
  RemoveAppointmentService,
  RemoveAppointmentSpecService,
  SetAppointment,
  SetEmployeeAppointment,
} from './appointment.actions';
import {
  BookingService,
  EmployeeProcedureService,
  EmployeeService,
  RoomService,
  getSecondsFromDateTime,
  timeStrToSeconds,
  transformDateTime,
  unixToDateTime,
} from '@beautyapp/core';
import { UpdateFormValue } from '@ngxs/form-plugin';
import { map, tap } from 'rxjs/operators';
import { combineLatest } from 'rxjs';
import { format } from 'date-fns';
import { EventInput } from '@fullcalendar/core';
import {
  mapEmployeeSchedulesToEvents,
  mapRoomSchedulesToEvents,
} from './utils/schedule-mapper';


/**
 * Compute a booking's calendar amounts from its per-procedure bookingServices,
 * mirroring the operations module filter (STANDARD serviceType, non-package).
 *
 * The booking-level `price`/`salePrice` are denormalized and drift when a booking
 * has multiple procedures and one is discounted (salePrice can collapse to a single
 * discounted procedure). Summing per-procedure prices fixes this (beaut-324).
 *
 * Package units count at what was paid for them (beaut-621): `employeeSalaryBase`
 * holds each unit's share of the package payment, with the rounding remainder
 * allocated (3 x 33.33 sold for 100 → 33.34 / 33.33 / 33.33). They used to be
 * dropped, and a package-only booking then fell back to the catalogue price, so
 * the employee's day read 99.99 for a 100 GEL package. A unit with no stored
 * share keeps its catalogue price; a package unit is never shown as discounted.
 *
 * Falls back to the existing booking-level `m.price`/`m.salePrice` when there are no
 * STANDARD procedures (e.g. minute-only bookings) or when `serviceType` is absent
 * (backend field added in a separate PR — degrades gracefully).
 */
export function computeCalendarBookingAmounts(m: any): { price: number; salePrice: number } {
  const std = (m?.bookingServices ?? []).filter(
    (bs: any) => bs?.service?.serviceType === 'STANDARD'
  );

  if (!std.length) {
    return { price: m?.price, salePrice: m?.salePrice };
  }

  const paidShare = (bs: any): number => +bs.employeeSalaryBase > 0 ? +bs.employeeSalaryBase : +bs.price;
  const original = (bs: any): number => (bs.packagePurchaseId ? paidShare(bs) : +bs.price);
  const final = (bs: any): number => (bs.packagePurchaseId ? paidShare(bs) : +bs.lastPrice);

  // Rounded to the tetri: 40 + 33.34 is 73.34000000000001 in floating point.
  const toTetri = (n: number) => Math.round(n * 100) / 100;
  const originalTotal = toTetri(std.reduce((acc: number, bs: any) => acc + original(bs), 0));
  const finalTotal = toTetri(std.reduce((acc: number, bs: any) => acc + final(bs), 0));

  return {
    price: originalTotal,
    salePrice: finalTotal < originalTotal ? finalTotal : 0
  };
}

export class AppointmentStateModel {
  booking?: {
    model?: {
      id?: string;
      cardNo?: string;
      employeeId?: string;
      onUserRequest: boolean;
      salaryFixed?: number;
      salaryPercent?: number;
      userId?: string;
      status: number;
      paymentType?: number;
      price: number;
      salePrice?: number;
      cash: number;
      card: number;
      minutes: number;
      balance: number;
      barter: number;
      consignation: number;
      bankTransfer: number;
      fixedPrice?: number;
      bookingDate: Date;
      startTime?: Date;
      endTime?: Date;
      isGift?: boolean;
      sendNotification?: boolean;
      isConsignation?: boolean;
      description?: string;
      insuranceId?: string;
      insurancePercent?: string;
      insurancePolicy?: string;
    };
  };
  employeeBooking: any;
  questionaire: any;
  services: any[] = [];
  specServices: any[] = [];
  products: any[] = [];
  events: any[] = [];
  roomEvents: any[] = [];
  scheduleEvents: any[] = [];
  onlineEvents: any[] = [];
  calendarPosition?: string;
  calendarInterval?: string;
  calendarEmployees?: any[] = [];
  roomCalendar?: any[] = [];
}

@State<AppointmentStateModel>({
  name: 'appointment',
  defaults: {
    booking: {
      model: {
        onUserRequest: false,
        status: 1,
        price: 0,
        cash: 0,
        card: 0,
        minutes: 0,
        balance: 0,
        barter: 0,
        consignation: 0,
        bankTransfer: 0,
        bookingDate: new Date(),
      },
    },
    employeeBooking: {
      model: undefined,
      dirty: false,
      status: '',
      errors: {},
    },
    questionaire: {
      model: undefined,
      dirty: false,
      status: '',
      errors: {},
    },
    products: [],
    services: [],
    specServices: [],
    events: [],
    roomEvents: [],
    scheduleEvents: [],
    onlineEvents: [],
    calendarPosition: 'resourceTimeline',
    calendarInterval: '00:15:00',
    calendarEmployees: [],
    roomCalendar: [],
  },
})
@Injectable()
export class AppointmentState {
  private bookingService = inject(BookingService);
  private employeeServiceService = inject(EmployeeProcedureService);
  private employeeService = inject(EmployeeService);
  private roomService = inject(RoomService);

  @Selector()
  static getServiceCount(state: AppointmentStateModel) {
    return state.services.length;
  }

  @Selector()
  static getSpecServiceCount(state: AppointmentStateModel) {
    return state.specServices.length;
  }

  @Selector()
  static getProductsCount(state: AppointmentStateModel) {
    return state.products.length;
  }

  @Selector()
  static getServiceSum(state: AppointmentStateModel) {
    return state.services.reduce((seconds, serv) => {
      const { salePrice, price } = serv;
      const priceTemp = +salePrice > 0 ? +salePrice : +price;
      return priceTemp + seconds;
    }, 0);
  }

  @Selector()
  static getServiceExpanseSum(state: AppointmentStateModel) {
    return state.services.reduce((seconds, serv) => {
      return (+serv.expence || 0) + seconds;
    }, 0);
  }

  @Selector()
  static getServices(state: AppointmentStateModel) {
    return state.services;
  }

  @Selector()
  static getSpecServices(state: AppointmentStateModel) {
    return state.specServices;
  }

  @Selector()
  static getProducts(state: AppointmentStateModel) {
    return state.products;
  }

  @Selector()
  static getAll(state: AppointmentStateModel) {
    return state;
  }

  @Selector()
  static getCalendarPosition(state: AppointmentStateModel) {
    return state.calendarPosition;
  }

  @Selector()
  static getCalendarInterval(state: AppointmentStateModel) {
    return state.calendarInterval;
  }

  @Selector()
  static getEvents(state: AppointmentStateModel) {
    return [...state.events, ...state.scheduleEvents];
  }

  @Selector()
  static getRoomEvents(state: AppointmentStateModel) {
    return [...state.roomEvents, ...state.scheduleEvents];
  }

  @Selector()
  static getScheduleEvents(state: AppointmentStateModel) {
    return state.scheduleEvents;
  }

  @Selector()
  static getOnlineEvents(state: AppointmentStateModel) {
    return state.onlineEvents;
  }

  @Selector()
  static getCalendarEmployees(state: AppointmentStateModel) {
    return state.calendarEmployees;
  }

  @Selector()
  static getRoomCalendar(state: AppointmentStateModel) {
    return state.roomCalendar;
  }

  @Action(AddAppointmentService)
  services(
    ctx: StateContext<AppointmentStateModel>,
    { service }: AddAppointmentService
  ) {
    const state = ctx.getState();

    const servicesQuantity = [];
    const { quantity } = service;
    for (let item = 0; item < +quantity; item++) {
      servicesQuantity.push(service);
    }

    const services = [...servicesQuantity, ...state.services];
    const startSeconds = getSecondsFromDateTime(
      state.booking?.model?.startTime
    );
    const totalSeconds = services.reduce((seconds, serv) => {
      return timeStrToSeconds(serv.time) + seconds;
    }, 0);
    const totalPrice = services.reduce((seconds, serv) => {
      return +serv.price + seconds;
    }, 0);
    const totalExpence = services.reduce((seconds, serv) => {
      return +serv.expence + seconds;
    }, 0);

    ctx.patchState({
      services: services,
    });
    // services.forEach((i: any) => {
    //   if (i.service.serviceProducts && i.service.serviceProducts.length) {
    //     const prod = i.service.serviceProducts.map(pr => ({
    //       ...pr,
    //       unitGram: pr.grSum,
    //       unitQuantity: pr.quantitySum
    //     }));
    //     ctx.dispatch(new AddAppointmentServiceProduct(prod));
    //   }
    // });
    ctx.dispatch(
      new UpdateFormValue({
        path: 'appointment.booking',
        value: {
          fixedPrice: totalExpence,
          endTime: new Date(unixToDateTime(startSeconds + totalSeconds * 60)),
          price: totalPrice,
        },
      })
    );
  }

  @Action(EditAppointmentService)
  editServices(
    ctx: StateContext<AppointmentStateModel>,
    { service }: EditAppointmentService
  ) {
    const state = ctx.getState();

    const index = service.serviceIndex;
    const services = [
      ...state.services.slice(0, index),
      service,
      ...state.services.slice(index + 1),
    ];
    const startSeconds = getSecondsFromDateTime(
      state.booking?.model?.startTime
    );
    const totalSeconds = services.reduce((seconds, serv) => {
      return timeStrToSeconds(serv.time) + seconds;
    }, 0);
    const totalPrice = services.reduce((seconds, serv) => {
      return +serv.price + seconds;
    }, 0);
    const totalExpence = services.reduce((seconds, serv) => {
      return +serv.expence + seconds;
    }, 0);

    // services.forEach(i => {
    //   if (i.service.serviceProducts && i.service.serviceProducts.length) {
    //     const prod = i.service.serviceProducts.map(pr => ({
    //       ...pr,
    //       unitGram: pr.grSum,
    //       unitQuantity: pr.quantitySum
    //     }));
    //     ctx.dispatch(new AddAppointmentServiceProduct(prod));
    //   }
    // });
    ctx.dispatch(
      new UpdateFormValue({
        path: 'appointment.booking',
        value: {
          fixedPrice: totalExpence,
        },
      })
    );
    ctx.dispatch(
      new UpdateFormValue({
        path: 'appointment.booking',
        value: {
          endTime: unixToDateTime(startSeconds + totalSeconds * 60),
        },
      })
    );
    ctx.dispatch(
      new UpdateFormValue({
        path: 'appointment.booking',
        value: {
          price: totalPrice,
        },
      })
    );
    ctx.patchState({
      services: services,
    });
  }

  @Action(RemoveAppointmentService)
  removeServices(
    ctx: StateContext<AppointmentStateModel>,
    { service }: RemoveAppointmentService
  ) {
    const state = ctx.getState();
    const index = state.services.findIndex(
      (idx) => idx.serviceId === service.serviceId
    );
    const services = [
      ...state.services.slice(0, index),
      ...state.services.slice(index + 1),
    ];

    const startSeconds = getSecondsFromDateTime(
      state.booking?.model?.startTime
    );
    const totalSeconds = services.reduce((seconds, serv) => {
      return timeStrToSeconds(serv.time) + seconds;
    }, 0);
    const totalPrice = services.reduce((seconds, serv) => {
      return serv.price + seconds;
    }, 0);
    ctx.dispatch(
      new UpdateFormValue({
        path: 'appointment.booking',
        value: {
          ...state.booking?.model,
          endTime: unixToDateTime(startSeconds + totalSeconds * 60),
          price: totalPrice,
        },
      })
    );
    ctx.patchState({
      services: services,
    });
    for (const i of services) {
      if (i.service.serviceProducts && i.service.serviceProducts.length) {
        const prod = i.service.serviceProducts.map((pr: any) => ({
          ...pr,
          unitGram: pr.grSum,
          unitQuantity: pr.quantitySum,
        }));
        ctx.dispatch(new AddAppointmentServiceProduct(prod));
      }
    }
  }

  @Action(AddAppointmentServiceProduct)
  addProducts(
    ctx: StateContext<AppointmentStateModel>,
    { products }: AddAppointmentServiceProduct
  ) {
    const state = ctx.getState();
    ctx.patchState({
      products: [...state.products, ...products],
    });
  }

  @Action(AddAppointmentProduct)
  addProduct(
    ctx: StateContext<AppointmentStateModel>,
    { product }: AddAppointmentProduct
  ) {
    const state = ctx.getState();
    ctx.patchState({
      products: [product, ...state.products],
    });
  }

  @Action(AddAppointmentSpecService)
  specServices(
    ctx: StateContext<AppointmentStateModel>,
    { specService }: AddAppointmentSpecService
  ) {
    const state = ctx.getState();
    const index = state.specServices.findIndex(
      (idx) => idx.serviceId === specService.serviceId
    );
    const specServices = [
      ...state.specServices.slice(0, index),
      ...state.specServices.slice(index + 1),
    ];
    ctx.patchState({
      specServices: specServices,
    });
  }

  @Action(RemoveAppointmentSpecService)
  removeSpecServices(
    ctx: StateContext<AppointmentStateModel>,
    { specService }: RemoveAppointmentSpecService
  ) {
    const state = ctx.getState();
    ctx.patchState({
      specServices: specService.specServices.filter(
        (item: any) => item.checked
      ),
    });
  }

  @Action(AddAppointment)
  addAppointment(ctx: StateContext<AppointmentStateModel>) {
    const state = ctx.getState();
    const params = {
      booking: state.booking?.model,
      questionaire: state.questionaire.model,
      services: state.services,
      specServices: state.specServices,
    };
    return this.bookingService.createNew(params);
  }

  @Action(AddEmployeeAppointment)
  addEmployeeAppointment(ctx: StateContext<AppointmentStateModel>) {
    const state = ctx.getState();
    return this.bookingService.createEmployeeNew(state.employeeBooking.model);
  }

  @Action(EditEmployeeAppointment)
  editEmployeeAppointment(ctx: StateContext<AppointmentStateModel>) {
    const state = ctx.getState();
    return this.bookingService.updateEmployeeNew(state.employeeBooking.model);
  }

  @Action(EditAppointment)
  editAppointment(ctx: StateContext<AppointmentStateModel>) {
    const state = ctx.getState();
    const params = {
      booking: state.booking?.model,
      questionaire: state.questionaire.model,
      services: state.services,
      specServices: state.specServices,
    };
    return this.bookingService.updateNew(state.booking?.model?.id, params);
  }

  @Action(SetAppointment)
  setAppointment(
    ctx: StateContext<AppointmentStateModel>,
    { bookingId }: SetAppointment
  ) {
    const state = ctx.getState();
    return this.bookingService.getById(bookingId).pipe(
      tap((res) => {
        // ctx.dispatch(new UpdateFormValue({
        //   path: 'appointment.booking',
        //   value: {
        //     ...res,
        //     startTime: new Date(`${res.bookingDate} ${res.startTime}`),
        //     endTime: new Date(`${res.bookingDate} ${res.endTime}`)
        //   }
        // }));

        ctx.patchState({
          services: res.services,
          specServices: res.specServices,
        });
      })
    );
  }

  @Action(CompleteUpdateAppointment)
  completeUpdateAppointment(ctx: StateContext<AppointmentStateModel>) {
    const state = ctx.getState();
    if (!state.booking?.model?.id) {
      return;
    }
    const params = {
      booking: {
        ...state.booking?.model,
        status: 2,
      },
      services: state.services,
      specServices: state.specServices,
    };
    return this.bookingService.updateNew(state.booking?.model?.id, params);
  }

  @Action(CompleteAppointment)
  completeAppointment(ctx: StateContext<AppointmentStateModel>) {
    const state = ctx.getState();
    const params = {
      booking: {
        ...state.booking?.model,
        status: 2,
      },
      questionaire: state.questionaire.model || null,
      services: state.services,
      specServices: state.specServices,
      products: state.products,
    };
    return this.bookingService.createNew(params).pipe(
      tap((novels) => {
        ctx.dispatch(
          new UpdateFormValue({
            path: 'appointment.booking',
            value: null,
          })
        );
        ctx.patchState({
          services: [],
          specServices: [],
        });
      })
    );
  }

  @Action(ClearAddAppointmentSpecService)
  clearAppointment(ctx: StateContext<AppointmentStateModel>) {
    const state = ctx.getState();
    ctx.dispatch(
      new UpdateFormValue({
        path: 'appointment.booking',
        value: null,
      })
    );
    ctx.patchState({
      services: [],
      specServices: [],
    });
  }

  @Action(ClearAppointmentService)
  clearAppointmentService(ctx: StateContext<AppointmentStateModel>) {
    ctx.patchState({
      services: [],
    });
  }

  @Action(ClearAddEmployeeAppointment)
  clearEmployeeAppointment(ctx: StateContext<AppointmentStateModel>) {
    ctx.dispatch(
      new UpdateFormValue({
        path: 'appointment.employeeBooking',
        value: null,
      })
    );
    ctx.dispatch(new ClearAppointmentService());
  }

  @Action(SetEmployeeAppointment)
  setEmployeeAppointment(
    ctx: StateContext<AppointmentStateModel>,
    { bookingId }: SetEmployeeAppointment
  ) {
    const state = ctx.getState();
    return this.bookingService.getById(bookingId).pipe(
      tap((res) => {
        if (res) {
          ctx.dispatch(
            new UpdateFormValue({
              path: 'appointment.employeeBooking',
              value: {
                ...res,
                bookingDate: new Date(res.bookingDate).toISOString(),
                startTime: format(
                  new Date(`${res.bookingDate} ${res.startTime}`),
                  'yyyy-MM-dd HH:mm:ss'
                ),
                endTime: format(
                  new Date(`${res.bookingDate} ${res.endTime}`),
                  'yyyy-MM-dd HH:mm:ss'
                ),
              },
            })
          );
        }
      })
    );
  }

  @Action(CalendarPosition)
  setCalendarPosition(
    ctx: StateContext<AppointmentStateModel>,
    { position }: CalendarPosition
  ) {
    const state = ctx.getState();
    ctx.patchState({
      calendarPosition: position,
    });
  }

  @Action(CalendarTimeInterval)
  setCalendarInterval(
    ctx: StateContext<AppointmentStateModel>,
    { interval }: CalendarTimeInterval
  ) {
    const state = ctx.getState();
    ctx.patchState({
      calendarInterval: interval,
    });
  }

  @Action(ChangeEmployee)
  async changeEmployee(
    ctx: StateContext<AppointmentStateModel>,
    { payload: { employee, oldEmployee } }: ChangeEmployee
  ) {
    const state = ctx.getState();

    const employeeServices = await this.employeeServiceService
      .getByIds(
        employee.id,
        state.services.map((s) => s.serviceId)
      )
      .toPromise();
    const services = state.services.map((service) => {
      if (service?.employeeId === oldEmployee?.id) {
        let outputPercent = employee?.outputPercent;
        let salaryFixed = 0;
        const find = employeeServices.find(
          (s: any) => s.serviceId === service.serviceId
        );
        if (find) {
          outputPercent = find.outputPercent;
          salaryFixed = find.fixedSalary;
        }

        return {
          ...service,
          employeeId: employee?.id,
          employeeName: employee?.user?.fullName,
          salaryPercent: outputPercent,
          salaryFixed: salaryFixed,
        };
      }
      return service;
    });

    if (employeeServices.length > 0) {
      // employeeServices.forEach((service: any) => {
      //   const idx = services.findIndex((s) => s.serviceId === service.serviceId);
      //   console.log(service.serviceId)
      //   console.log(idx)
      //   if (idx !== -1) {
      //     services[idx] = {
      //       ...services[idx],
      //       salaryPercent: +service.outputPercent || 0,
      //       salaryFixed: +service.fixedSalary || 0
      //     };
      //   }
      // });
      // services.forEach((service) => {
      //   const find = employeeServices.find((s: any) => s.serviceId === service.serviceId);
      //   if (find) {
      //     service.salaryPercent = employee?.outputPercent || 0
      //     service.salaryFixed = employee?.fixedSalary || 0
      //   }
      // })
    }
    ctx.patchState({
      services: services,
    });
  }

  @Action(GetEvents)
  getEvents(ctx: StateContext<AppointmentStateModel>, { payload }: GetEvents) {
    const params = {
      date: payload.date,
    };
    return this.bookingService.getCalendarBookingNewV2(params).pipe(
      tap((res) => {
        ctx.patchState({
          events: res.flatMap((m: any) => {
            // beaut-463: a BookingHub booking is coloured by its PROCEDURE, like
            // every other booking. It used to be forced to BookingHub's brand red,
            // which told the operator where it came from at the cost of the one
            // thing the calendar colour is for — reading the day by service type.
            // The "ონლაინ" marker already says it arrived online (day-grid keys
            // that off onlineBookingId), so the colour did not need to say it too.
            const backgroundColor = m.visit?.id
              ? m.bookingServices && m.bookingServices.length
                ? m.bookingServices[0]?.service?.serviceCategory?.color
                : null
              : m.service?.serviceCategory?.color || null;

            const services = m.visit?.id
              ? m.bookingServices?.length
                ? m.bookingServices.map((s: any) => s.service?.name).join(', ')
                : ''
              : m.services?.map((s: any) => s.service?.name).join(', ');

            const { price: calendarPrice, salePrice: calendarSalePrice } =
              computeCalendarBookingAmounts(m);

            const primaryEvent = {
              id: m.id,
              resourceId: m.employee?.id,
              start: transformDateTime(m.bookingDate, m.startTime),
              end: transformDateTime(m.bookingDate, m.endTime),
              title: m.visit?.isAnonymous
                ? m.visit?.anonymousName + '(უცნობი)'
                : m.user?.fullName,
              backgroundColor: backgroundColor,
              groupId: m.visit?.id,
              oldStyle: !!m.visit?.id,
              extendedProps: {
                bookingDate: m.bookingDate,
                roomId: m.roomId,
                roomName: m.room?.name,
                employeeId: m.employee?.id,
                employeeName: m.employee?.user?.fullName,
                fullName: m.user?.fullName,
                balance: m.user?.balance,
                userId: m.user?.id,
                mobilePhone: m.user?.mobilePhone,
                status: m.status,
                visitStatus: m.visit?.status || null,
                onUserRequest: m.onUserRequest,
                price: calendarPrice,
                salePrice: calendarSalePrice,
                description: m.description,
                latestComment: m.latestComment || null,
                commentCount: m.commentCount || 0,
                createdAt: m.createdAt,
                createdBy: m.createdBy?.fullName,
                completedBy: m.completedBy?.fullName,
                isAnonymous: m.visit?.isAnonymous,
                anonymousName: m.visit?.anonymousName + '(უცნობი)',
                services: services,
                source: m.source,
                onlineBookingId: m.onlineBookingId ?? null,
                // Which system the online booking came from. The calendar marker
                // shows the BookingHub logo for 'bookinghub*' and the generic
                // globe otherwise — a salon's own online booking is not
                // BookingHub and must not be badged as one.
                onlineSource: m.onlineSource ?? null,
                onlinePaidAmount: m.onlinePaidAmount ?? null,
                onlineOriginalAmount: m.onlineOriginalAmount ?? null,
                additionalEmployees: null as string[] | null,
              },
            };

            // Collect additional employee info from bookingServices
            const additionalEmployeeMap = new Map<string, string>();
            m.bookingServices?.forEach((bs: any) => {
              bs.additionalEmployees?.forEach((ae: any) => {
                if (ae.employeeId && ae.employeeId !== m.employee?.id) {
                  additionalEmployeeMap.set(
                    ae.employeeId,
                    ae.employee?.user?.fullName || ''
                  );
                }
              });
            });

            // Add additional employee names to primary event
            const additionalEmployeeNames = Array.from(
              additionalEmployeeMap.values()
            ).filter(Boolean);
            if (additionalEmployeeNames.length) {
              primaryEvent.extendedProps.additionalEmployees =
                additionalEmployeeNames;
            }

            // Create events for additional employees
            const additionalEvents = Array.from(
              additionalEmployeeMap.keys()
            ).map((employeeId) => ({
              ...primaryEvent,
              id: `${m.id}-${employeeId}`,
              resourceId: employeeId,
              extendedProps: {
                ...primaryEvent.extendedProps,
                isAdditionalEmployee: true,
              },
            }));

            return [primaryEvent, ...additionalEvents];
          }),
        });
      })
    );
  }

  @Action(GetRoomEvents)
  getRoomEvents(
    ctx: StateContext<AppointmentStateModel>,
    { payload }: GetEvents
  ) {
    const params = {
      date: payload.date,
    };
    return this.bookingService.getCalendarBookingNewV2(params).pipe(
      tap((res) => {
        ctx.patchState({
          roomEvents: res
            .filter((ev: any) => ev.roomId)
            .map((m: any) => {
              // beaut-463 — same rule as above; this is the second mapping of the
              // same events and drifting the two apart is how one calendar view
              // ends up red and the other not.
              const backgroundColor = m.visit?.id
                ? m.bookingServices && m.bookingServices.length
                  ? m.bookingServices[0]?.service?.serviceCategory?.color
                  : null
                : m.service?.serviceCategory?.color || null;

              const services = m.visit?.id
                ? m.bookingServices?.length
                  ? m.bookingServices
                      .map((s: any) => s.service?.name)
                      .join(', ')
                  : ''
                : m.services?.map((s: any) => s.service?.name).join(', ');

              // Collect additional employee names
              const additionalEmployeeNames: string[] = [];
              m.bookingServices?.forEach((bs: any) => {
                bs.additionalEmployees?.forEach((ae: any) => {
                  if (
                    ae.employeeId &&
                    ae.employeeId !== m.employee?.id &&
                    ae.employee?.user?.fullName
                  ) {
                    additionalEmployeeNames.push(ae.employee.user.fullName);
                  }
                });
              });

              const { price: calendarPrice, salePrice: calendarSalePrice } =
                computeCalendarBookingAmounts(m);

              return {
                id: m.id,
                resourceId: m.roomId,
                start: transformDateTime(m.bookingDate, m.startTime),
                end: transformDateTime(m.bookingDate, m.endTime),
                title: m.visit?.isAnonymous
                  ? m.visit?.anonymousName + '(უცნობი)'
                  : m.user?.fullName,
                backgroundColor: backgroundColor,
                groupId: m.visit?.id,
                oldStyle: !!m.visit?.id,
                extendedProps: {
                  bookingDate: m.bookingDate,
                  roomId: m.roomId,
                  roomName: m.room?.name,
                  employeeId: m.employee?.id,
                  employeeName: m.employee?.user?.fullName,
                  additionalEmployees: additionalEmployeeNames.length
                    ? additionalEmployeeNames
                    : undefined,
                  fullName: m.user?.fullName,
                  balance: m.user?.balance,
                  userId: m.user?.id,
                  mobilePhone: m.user?.mobilePhone,
                  status: m.status,
                  visitStatus: m.visit?.status || null,
                  onUserRequest: m.onUserRequest,
                  price: calendarPrice,
                  salePrice: calendarSalePrice,
                  description: m.description,
                  latestComment: m.latestComment || null,
                  commentCount: m.commentCount || 0,
                  createdAt: m.createdAt,
                  createdBy: m.createdBy?.fullName,
                  completedBy: m.completedBy?.fullName,
                  isAnonymous: m.visit?.isAnonymous,
                  anonymousName: m.visit?.anonymousName + '(უცნობი)',
                  services: services,
                  source: m.source,
                },
              };
            }),
        });
      })
    );
  }

  @Action(GetOnlineEvents)
  getOnlineEvents(
    ctx: StateContext<AppointmentStateModel>,
    { payload }: GetOnlineEvents
  ) {
    return combineLatest([
      this.bookingService.onlineBookings(payload.date),
      this.bookingService.getCalendarBookingNewV2({
        date: payload.date,
      }),
    ]).pipe(
      tap(([onlineEvents, events]) => {
        const allEvents = [];
        if (onlineEvents.length) {
          allEvents.push(
            ...onlineEvents.map((onlineEvent: any) => {
              return {
                id: onlineEvent.id,
                title: `${onlineEvent?.user?.fullName} - ${onlineEvent?.user?.mobilePhone}`,
                start: transformDateTime(
                  onlineEvent.bookingDate,
                  onlineEvent.startTime
                ),
                end: transformDateTime(
                  onlineEvent.bookingDate,
                  onlineEvent.endTime
                ),
                resourceId: onlineEvent.employeeId,
                extendedProps: {
                  user: onlineEvent.user,
                  employeeId: onlineEvent.employee.id,
                  employee: onlineEvent.employee,
                  employeeName: onlineEvent.employee.user.fullName,
                  bookingDate: onlineEvent.bookingDate,
                  description: onlineEvent.description,
                  services: onlineEvent.services,
                },
              };
            })
          );
        }
        if (events.length) {
          allEvents.push(
            ...events.map((m: any) => {
              return {
                id: m.booking_id,
                resourceId: m.employee_id,
                start: transformDateTime(
                  m.booking_bookingDate,
                  m.booking_startTime
                ),
                end: transformDateTime(
                  m.booking_bookingDate,
                  m.booking_endTime
                ),
                display: 'background',
                title: 'დაკავებული დრო',
                extendedProps: {
                  disabled: true,
                  bookingDate: m.booking_bookingDate,
                  employeeName: m.employeeUser_fullName,
                  fullName: m.user_fullName,
                  userId: m.user_id,
                  mobilePhone: m.user_mobilePhone,
                  status: m.booking_status,
                  onUserRequest: m.booking_onUserRequest,
                  price: m.booking_price,
                  salePrice: m.booking_salePrice,
                  description: m.booking_description,
                  createdAt: m.booking_created_at,
                  createdBy: m.createdBy_fullName,
                  completedBy: m.completedBy_fullName,
                  services: m.booking_services
                    .map((s: any) => s.service.name)
                    ?.join(', '),
                },
                // beaut-463, third site. This one maps the PENDING online-booking
                // overlay rather than accepted bookings, so it is arguably a
                // different case — but it paints onto the same calendar, and
                // leaving one class of BookingHub red there would undo the point
                // of the other two. Kept as its own commit so it can be reverted
                // alone if pending bookings turn out to want their own colour.
                backgroundColor: m.serviceCategory_color || null,
              };
            })
          );
        }
        ctx.patchState({
          onlineEvents: allEvents,
        });
      })
    );
  }

  @Action(GetCalendarEmployee)
  getCalendarEmployee(
    ctx: StateContext<AppointmentStateModel>,
    { payload }: GetCalendarEmployee
  ) {
    return this.employeeService.getEmployeeToCalendar(payload.date).pipe(
      map((i: any) => {
        return i.map((m: any) => {
          return {
            id: m.id,
            title: m.fullName,
            userId: m.userId,
            icon: 'fa fa-user',
            schedule: m.schedule,
          };
        });
      }),
      tap((res) => {
        ctx.patchState({
          calendarEmployees: res,
          scheduleEvents: mapEmployeeSchedulesToEvents(res),
        });
      })
    );
  }

  @Action(GetRoomCalendar)
  getRoomCalendar(
    ctx: StateContext<AppointmentStateModel>,
    { payload }: GetRoomCalendar
  ) {
    return this.roomService.getRoomsForCalendar({ date: payload.date }).pipe(
      map((rooms: any[]) => {
        return rooms.map((room: any) => {
          return {
            id: room.id,
            title: room.name,
            extendedProps: {
              canWorkMultipleEmployees: room.canWorkMultipleEmployees,
              description: room.description,
              schedule: room.schedule,
            },
          };
        });
      }),
      tap((res) => {
        ctx.patchState({
          roomCalendar: res,
          scheduleEvents: mapRoomSchedulesToEvents(res),
        });
      })
    );
  }
}
